import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@hooks/useAuth';
import { getUserPermissions } from '@/features/configuration';

interface PermissionsContextType {
  permissions: string[];
  isLoaded: boolean;
  loadPermissions: () => Promise<void>;
  clearPermissions: () => void;
  refreshPermissions: () => Promise<void>;
  can: (module: string, action: string) => boolean;
  canAny: (module: string, actions: string[]) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadPermissions = async (bypassCache = false) => {
    if (import.meta.env.DEV) {
      console.log('[Permissions] loadPermissions called | user.id:', user?.id, '| user.tenantId:', user?.tenantId);
    }

    if (!user?.id || !user?.tenantId) {
      if (import.meta.env.DEV) {
        console.warn('[Permissions] Missing user.id or user.tenantId — skipping permission load');
      }
      // Keep isLoaded false so consumers wait — do NOT prematurely set isLoaded=true
      return;
    }

    try {
      // Try to load from sessionStorage first
      const cached = sessionStorage.getItem('hrms_permissions');
      if (!bypassCache && cached) {
        const parsed = JSON.parse(cached);
        if (import.meta.env.DEV) {
          console.log('[Permissions] Loaded from sessionStorage cache:', parsed);
        }
        setPermissions(parsed);
        setIsLoaded(true);
        return;
      }

      // Fetch from API
      if (import.meta.env.DEV) {
        console.log('[Permissions] Fetching from API...');
      }
      const result = await getUserPermissions(user.id, user.tenantId);
      if (import.meta.env.DEV) {
        console.log('[Permissions] API response:', result);
      }
      setPermissions(result.permissions);
      sessionStorage.setItem('hrms_permissions', JSON.stringify(result.permissions));
      setIsLoaded(true);
    } catch (error) {
      console.error('[Permissions] Failed to load permissions:', error);
      setPermissions([]);
      setIsLoaded(true);
    }
  };

  const clearPermissions = () => {
    setPermissions([]);
    setIsLoaded(false);
    sessionStorage.removeItem('hrms_permissions');
  };

  // Force re-fetch from API, bypassing sessionStorage cache
  // Call this after admin changes a user's permissions via PermissionsMatrix
  const refreshPermissions = async () => {
    sessionStorage.removeItem('hrms_permissions');
    setIsLoaded(false);
    await loadPermissions(true);
  };

  const can = (module: string, action: string): boolean => {
    const permission = `${module.toLowerCase()}.${action.toLowerCase()}`;
    const result = permissions.includes(permission);
    if (import.meta.env.DEV) {
      console.log(`[can] "${permission}" → ${result ? '✅ GRANTED' : '❌ DENIED'} | Total permissions loaded: ${permissions.length}`);
    }
    return result;
  };

  const canAny = (module: string, actions: string[]): boolean => {
    return actions.some(action => can(module, action));
  };

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[Permissions] useEffect fired | isAuthenticated:', isAuthenticated, '| user:', user?.id ?? 'null');
    }
    if (isAuthenticated && user) {
      loadPermissions();
    } else {
      clearPermissions();
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Re-fetch permissions when the user returns to the tab, to pick up changes an
    // admin may have made. Two things are deliberate here:
    //  1. We use loadPermissions(true) — a SILENT refresh that updates permissions
    //     without flipping isLoaded to false. refreshPermissions() sets isLoaded=false,
    //     which makes route/permission guards (e.g. SettingsGuard) unmount the whole
    //     subtree — that closes any open dialog and wipes its local state.
    //  2. We only listen to 'visibilitychange', NOT window 'focus'. The window 'focus'
    //     event also fires when the OS file-picker closes (picking a file doesn't hide
    //     the document), which would otherwise reset a screen mid-upload.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadPermissions(true).catch((error) => {
          console.error('[Permissions] Failed to refresh permissions on visibility change:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, user?.id, user?.tenantId]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        isLoaded,
        loadPermissions,
        clearPermissions,
        refreshPermissions,
        can,
        canAny,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermission() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermission must be used within a PermissionsProvider');
  }
  return context;
}

